<template>
  <div>
    <!-- Product Grid -->
    <div :class="[
      'grid gap-2',
      isMobile ? 'grid-cols-2' : 'grid-cols-3 md:grid-cols-3'
    ]">
      <ProductCard
        v-for="product in paginatedProducts"
        :key="product.id"
        :product="product"
        :isMobile="isMobile"
        @add-to-cart="$emit('add-to-cart', $event)"
        @show-detail="$emit('show-detail', $event)"
      />
    </div>

    <!-- Improved Pagination -->
    <div class="mt-4 flex justify-center items-center gap-1">
      <!-- First and Previous buttons -->
      <button
        v-if="currentPage > 1"
        @click="changePage(1)"
        class="px-2 py-1 border rounded text-sm hover:bg-gray-100"
      >
        «
      </button>
      <button
        v-if="currentPage > 1"
        @click="changePage(currentPage - 1)"
        class="px-2 py-1 border rounded text-sm hover:bg-gray-100"
      >
        ‹
      </button>

      <!-- Page numbers with ellipsis -->
      <template v-for="n in getPageNumbers">
        <div :key="n">
          <button
            v-if="n !== '...'"
            @click="changePage(n)"
            :class="[
              'px-3 py-1 border rounded text-sm',
              currentPage === n
                ? 'bg-yellow-400 border-yellow-400 text-black font-medium'
                : 'bg-white hover:bg-gray-100'
            ]"
          >
            {{ n }}
          </button>
          <span
            v-else
            class="px-2 text-gray-500"
          >
            {{ n }}
          </span>
        </div>
      </template>

      <!-- Next and Last buttons -->
      <button
        v-if="currentPage < totalPages"
        @click="changePage(currentPage + 1)"
        class="px-2 py-1 border rounded text-sm hover:bg-gray-100"
      >
        ›
      </button>
      <button
        v-if="currentPage < totalPages"
        @click="changePage(totalPages)"
        class="px-2 py-1 border rounded text-sm hover:bg-gray-100"
      >
        »
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import ProductCard from './ProductCard.vue'

export default {
  name: 'ProductList',
  components: {
    ProductCard
  },
  props: {
    products: {
      type: Array,
      required: true
    },
    isMobile: {
      type: Boolean,
      required: true
    }
  },
  emits: ['add-to-cart', 'show-detail'],
  setup(props) {
    const itemsPerPage = 21
    const currentPage = ref(1)
    
    const totalPages = computed(() => {
      return Math.ceil(props.products.length / itemsPerPage)
    })

    const paginatedProducts = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage
      const end = start + itemsPerPage
      return props.products.slice(start, end)
    })

    const getPageNumbers = computed(() => {
      const pages = []
      const maxVisiblePages = 5 // Jumlah halaman yang ditampilkan

      if (totalPages.value <= maxVisiblePages) {
        // Jika total halaman kurang dari atau sama dengan maxVisiblePages,
        // tampilkan semua nomor halaman
        for (let i = 1; i <= totalPages.value; i++) {
          pages.push(i)
        }
      } else {
        // Selalu tampilkan halaman pertama
        pages.push(1)

        if (currentPage.value <= 3) {
          // Jika halaman saat ini dekat dengan awal
          for (let i = 2; i <= 4; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(totalPages.value)
        } else if (currentPage.value >= totalPages.value - 2) {
          // Jika halaman saat ini dekat dengan akhir
          pages.push('...')
          for (let i = totalPages.value - 3; i <= totalPages.value; i++) {
            pages.push(i)
          }
        } else {
          // Jika halaman saat ini di tengah
          pages.push('...')
          pages.push(currentPage.value - 1)
          pages.push(currentPage.value)
          pages.push(currentPage.value + 1)
          pages.push('...')
          pages.push(totalPages.value)
        }
      }

      return pages
    })

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }

    const changePage = (page) => {
      currentPage.value = page
      scrollToTop()
    }

    return {
      currentPage,
      totalPages,
      paginatedProducts,
      getPageNumbers,
      changePage
    }
  }
}
</script>